'use client'

import { useEffect } from 'react'

import ExclamationCircle from '@/components/icons/exclamation-circle'
import InfoCircle from '@/components/icons/info-circle'
import { trackEvent } from '@/lib/mixpanel'

/**
 * Universal error component to display multiple types of errors.
 * Example Usage:
 ```
  <Error
    icon="info"
    size="popup"
    position="right"
    hidden={false}
    className="bg-red-600 text-shades-0 my-16"
    error={{
      code: 'DEV',
      message: 'This is a test',
    }}
  />

  <Error
    icon="info"
    size="popup"
    position="right"
    hidden={false}
    className="bg-red-600 text-shades-0 my-16"
  >
    <h3 className="text-xl font-semibold leading-tight">
      This is a fancy error!
    </h3>
    <p>This is a test on how to use the component with children elements.</p>
  </Error>
 ```
 */
export default function Error({
  /**
   * The error object
   * - TODO: support more error types that display correctly
   * @type {object}
   */
  error = {},
  /**
   * The size of the loader.
   * @type {string}
   */
  size = 'alert',
  /**
   * The icon we will use for the error.
   * - Options: info, exclamation
   * @type {string}
   */
  icon,
  /**
   * Whether or not to hide the error.
   * - TODO: Will eventually report the error and not show it to the user.
   * @type {boolan}
   */
  hidden = false,
  /**
   * Classes to add to elements
   * @type {string}
   */
  className = '',
  /**
   * The position of the popup that shows the error.
   * @type {string}
   */
  popupPosition = 'left',
  /**
   * Skips console logs
   * @type {boolean}
   */
  skipConsole = false,
  /**
   * Child components and HTML
   * @type {HTML | JSX}
   */
  children,
}) {
  let classArray = [`error-${size}`, 'stroke-white']
  if (hidden === true) classArray.push('invisible')
  classArray.push(className)
  const classes = classArray.join(' ')

  function IconComponent(props) {
    const icons = {
      info: InfoCircle,
      exclamation: ExclamationCircle,
    }
    const Component = icons[icon] || icons.exclamation
    return <Component {...props} />
  }

  useEffect(() => {
    if (!skipConsole) {
      console.error('<Error /> %O', error)
    }
    trackEvent('Error', { error: error })
  }, [])

  const elements = {
    alert: (
      <div
        className={`rounded-sm border border-neutral-blue-600 bg-neutral-blue-800 p-4 text-shades-0 ${classes}`}
      >
        <div>
          {children || (
            <>
              <IconComponent className="h-6 w-6" />
              <span>
                {error.code ? `[${error.code}] ` : ''}
                {error.message || 'Unknown error. Check logs.'}
              </span>
            </>
          )}
        </div>
      </div>
    ),
    hero: (
      <div className={`rounded-lg ${classes}`}>
        <div className="mx-auto flex max-w-2xl flex-col content-center py-10 text-center">
          {children || (
            <>
              <h1 className="text-h-1 font-bold">
                <IconComponent className="h-36 w-36 stroke-white" />
              </h1>
              <p className="py-6">
                {error.code ? `[${error.code}] ` : ''}
                {error.message || 'Unknown error. Check logs.'}
              </p>
            </>
          )}
        </div>
      </div>
    ),
    popup: (
      <div className="group relative inline-block">
        <button
          type="button"
          aria-label="Show error details"
          className="m-1 cursor-pointer text-error"
        >
          <IconComponent />
        </button>
        <div
          role="tooltip"
          className={`absolute top-0 z-50 hidden w-64 rounded-md border border-neutral-blue-700 bg-error p-2 text-shades-0 shadow-sm group-focus-within:block group-hover:block ${
            popupPosition === 'right' ? 'left-full ml-2' : 'right-full mr-2'
          }`}
        >
          <div className="flex flex-col gap-3">
            {children || (
              <>
                {error.code && (
                  <h3 className="text-xl font-semibold leading-tight">
                    Code: {error.code}
                  </h3>
                )}
                <p>{error.message || 'Unknown error. Check logs.'}</p>
              </>
            )}
          </div>
        </div>
      </div>
    ),
    inline: (
      <>
        <span className={`text-error ${className}`}>
          <IconComponent className="mr-2 inline-block h-6 w-6" />
          <span className="mr-2">[{error.code || 'UNKNOWN'}]</span>
          {error.message || 'Unknown error. Check logs.'}
        </span>
      </>
    ),
  }

  return elements[size]
}
